import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, output } from '@angular/core';
import { ChatContentService } from '../../chat-content.service';
import { ChatStateStoreService } from '../../chat-state-store/chat-state-store.service';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IGetChatTokenResponse, ITaxIdAndName } from '../../chat.models';
import { InputNumericDirective } from 'src/app/shared/directives/input-numeric.directive';
import { maxDateValidator, minDateValidator } from '../../../../form-validators/date-validators'
import { Observable, switchMap, tap } from 'rxjs';

interface IChatValidationForm {
  dob: FormControl<string>;
  memberId: FormControl<string>;
  providerNpi: FormControl<string>;
  securitySetting: FormControl<string>;
}

@Component({
  selector: 'app-chat-validation-questions',
  standalone: true,
  imports: [InputNumericDirective, ReactiveFormsModule],
  templateUrl: './chat-validation-questions.component.html',
  styleUrl: './chat-validation-questions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChatValidationQuestionsComponent {
  readonly securitySettings = input.required<ITaxIdAndName[]>();
  readonly teamSelection_ = input('');

  readonly chatTokenAcquired = output<string>();

  private _chatContentService = inject(ChatContentService);
  private _chatStateStoreService = inject(ChatStateStoreService);

  private _fb = new FormBuilder();
  private readonly _MAX_DATE = new Date();
  private readonly _MIN_DATE = new Date('1900-01-01T00:00:00'); 

  protected form: FormGroup<IChatValidationForm> = this._fb.group({
    dob: ['', [Validators.required, maxDateValidator(this._MAX_DATE), minDateValidator(this._MIN_DATE)]],
    memberId: ['', [Validators.required, Validators.minLength(11)]],
    providerNpi: [''],
    securitySetting: ['']
  });  
  protected readonly formattedMaxDate = this._formatDate(this._MAX_DATE);
  protected readonly formattedMinDate = this._formatDate(this._MIN_DATE);
  protected readonly isPharmacyTeam_ = computed(() => this.teamSelection_().toLowerCase() === 'pharmacy');

  constructor() {
    effect(() => {
      const providerNpi = this.form.get('providerNpi');
      const securitySetting = this.form.get('securitySetting');
      
      if (this.isPharmacyTeam_()) {
        providerNpi.setValidators([Validators.required, Validators.minLength(10)]);
        securitySetting.clearValidators();
      } else {
        providerNpi.clearValidators();
        securitySetting.setValidators([Validators.required]);
      }

      providerNpi.updateValueAndValidity();
      securitySetting.updateValueAndValidity();
    });
  }

  protected controlHasError(controlName: 'memberId' | 'dob' | 'securitySetting' | 'providerNpi'): boolean {
    return this.form.get(controlName).invalid && (this.form.get(controlName).touched || this.form.get(controlName).dirty);
  }

  protected handleSecuritySettingsValueChange(e: Event) {
    const value = (e.target as HTMLSelectElement).value;

    this.form.get('securitySetting').setValue(value);
  }

  protected isButtonDisabled(): boolean {
    return this.form.invalid;
  }

  protected markSecuritySetting(): void {
    this.form.get('securitySetting').markAsTouched();
    this.form.get('securitySetting').markAsDirty();
  }

  protected submit() {
    const dob = this.form.get('dob').value;
    const memberId = this.form.get('memberId').value;
    // Either vnumber or providerNpi will be set to a non-empty string based on the team selection (not both)
    const vnumber = this.form.get('securitySetting').value; // for clinical operations & provider services
    const providerNPI = this.form.get('providerNpi').value; // for pharmacy team

    this._chatStateStoreService.updatePartialChatState({ dob, memberId, providerNPI, vnumber });

    this._getChatToken().subscribe();
  }

  private _formatDate(date: Date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  private _getChatToken(): Observable<IGetChatTokenResponse> {
    return this._chatStateStoreService.chatState$
      .pipe(
        switchMap(res => this._chatContentService.getChatToken(res)),
        tap(res => this.chatTokenAcquired.emit(res?.token))
      )
  }
}
